
export function pickPrefixedLine(body, label) {
  if (!body) return null;
  const esc = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^\\s*${esc}\\s*:\\s*(.*)$`, "im");
  const mm = body.match(re);
  return mm ? mm[1].trim() : null;
}

/** Formatter / markdown style: `**Subject:** value` */
function pickMarkdownBoldField(body, label) {
  if (!body) return null;
  const esc = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^\\s*\\*\\*${esc}\\*\\*\\s*:\\s*(.+)$`, "im");
  const mm = body.match(re);
  return mm ? mm[1].trim() : null;
}

function pickFieldEither(body, label) {
  return pickPrefixedLine(body, label) || pickMarkdownBoldField(body, label);
}

function stripHeaderLines(body) {
  if (!body) return "";
  return body
    .replace(/^\s*(Subject|From|To|Date|CC|BCC|Report Date)\s*:.*$/gim, "")
    .replace(
      /^\s*\*\*(Subject|From|To|Date|Participants|CC|BCC|Report Date)\*\*\s*:.*$/gim,
      ""
    )
    .replace(/^\n+/, "")
    .trim();
}

/** Strip inline **Subject:** / **To:** / **Participants:** blobs (same line as body start). */
function stripInlineBoldEmailMeta(body) {
  if (!body) return "";
  let b = body.trim();
  // Same-line: **Subject:** x **To:** y …
  b = b.replace(
    /^\s*\*\*Subject:\*\*[^\n]+\s*\*\*To:\*\*[^\n]+\s*/i,
    ""
  );
  // Optional **Participants:** … **Date:** … on following lines or same block
  b = b.replace(/^\s*\*\*Participants:\*\*[^\n]+\s*/i, "");
  b = b.replace(/^\s*\*\*Date:\*\*[^\n]+\s*/i, "");
  b = b.replace(/^\s*\*\*Report Date:\*\*[^\n]+\s*/i, "");
  return b.trim();
}

function extractEmailFields(rawBody) {
  const stripped = stripHeaderLines(rawBody);
  return {
    subject: pickFieldEither(rawBody, "Subject"),
    from: pickFieldEither(rawBody, "From"),
    to: pickFieldEither(rawBody, "To"),
    date: pickFieldEither(rawBody, "Date"),
    body: stripInlineBoldEmailMeta(stripped),
  };
}

/**
 * `Email 1 from Sahil Pugalia (2026-03-18)` (after stripping `### `)
 * @returns {{ from: string|null, date: string|null }}
 */
export function parseEmailHeadingFromDate(titleLine) {
  const t = (titleLine || "").trim();
  const m = t.match(/^Email\s+\d+\s+from\s+(.+?)\s*\(([^)]+)\)\s*$/i);
  if (m) return { from: m[1].trim(), date: m[2].trim() };
  return { from: null, date: null };
}

export function parseEmailThreadEqualsFormat(threadText) {
  const firstIdx = threadText.search(/={10,}\s*\nEmail\s+\d+\s+of\s+\d+/);
  if (firstIdx === -1) return null;
  const preamble = firstIdx > 0 ? threadText.slice(0, firstIdx).trim() : "";
  const headerRe = /={10,}\s*\nEmail\s+(\d+)\s+of\s+(\d+)\s*\n={10,}\s*\n/g;
  const emails = [];
  let m;
  while ((m = headerRe.exec(threadText)) !== null) {
    const start = m.index + m[0].length;
    const rest = threadText.slice(start);
    const nextRel = rest.search(/\n={10,}\s*\nEmail\s+\d+\s+of\s+\d+/);
    const len = nextRel === -1 ? rest.length : nextRel;
    const rawBody = rest.slice(0, len).trim();
    const fields = extractEmailFields(rawBody);
    emails.push({
      title: `Email ${m[1]} of ${m[2]}`,
      ...fields,
    });
  }
  return emails.length ? { preamble, emails } : null;
}

export function parseEmailThreadHashFormat(threadText) {
  if (!/(^|\n)### Email\s+\d+/.test(threadText)) return null;
  const sections = threadText.split(/\n(?=### Email\s+\d+)/g).filter(Boolean);
  const threadHeader = sections[0]?.startsWith("### Email") ? "" : sections[0];
  const emailSections = sections[0]?.startsWith("### Email") ? sections : sections.slice(1);
  return { threadHeader, emailSections };
}

/**
 * @param {string} threadText
 * @returns {{ variant: string, subject: string|null, emails: Array<{title,from,to,date,subject,body}> }}
 */
export function parseFormattedEmailThread(threadText) {
  const text = threadText || "";
  const equals = parseEmailThreadEqualsFormat(text);
  if (equals) {
    const subject = equals.emails[0]?.subject || null;
    return {
      variant: "equals",
      subject,
      emails: equals.emails,
    };
  }
  const hash = parseEmailThreadHashFormat(text);
  if (hash) {
    const { threadHeader, emailSections } = hash;
    const subject =
      pickMarkdownBoldField(threadHeader, "Subject") ||
      pickPrefixedLine(threadHeader, "Subject") ||
      threadHeader.match(/\*\*Subject:\*\*\s*(.+)/)?.[1]?.trim() ||
      threadHeader.match(/Subject\s*:\s*(.+)/i)?.[1]?.trim() ||
      null;
    const emails = emailSections.map((section, idx) => {
      const lines = section.split("\n");
      const title =
        lines[0]?.replace(/^#{1,6}\s*/, "").trim() || `Email ${idx + 1}`;
      const titleForParse = title.replace(/^###\s*/, "").trim();
      const { from: fromTitle, date: dateTitle } = parseEmailHeadingFromDate(titleForParse);
      const rawBody = lines.slice(1).join("\n").trim();
      const fields = extractEmailFields(rawBody);
      return {
        title: titleForParse,
        from: fields.from || fromTitle,
        to: fields.to,
        date: fields.date || dateTitle,
        subject: fields.subject,
        body: fields.body,
      };
    });
    emails.forEach((e) => {
      if (!e.subject && subject) e.subject = subject;
    });
    return { variant: "hash", subject, emails };
  }
  return {
    variant: "plain",
    subject: null,
    emails: [{ title: "Content", from: null, to: null, date: null, subject: null, body: text }],
  };
}
